import { apiUrl } from './http';
import { peekTenantRuntime } from '../config/tenant';
/**
 * Cloud Logging Service - Centralized log collection and forwarding
 * Captures frontend console logs and sends to Google Cloud Logging
 */

import { auth } from '../config/firebase';

interface LogEntry {
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'debug';
  message: string;
  source: 'frontend' | 'backend';
  component: string;
  userId?: string;
  sessionId: string;
  url: string;
  userAgent: string;
  metadata?: Record<string, any>;
  stackTrace?: string;
}

interface CloudLoggingConfig {
  projectId: string;
  logName: string;
  batchSize: number;
  flushInterval: number;
  enabled: boolean;
}

class CloudLoggingService {
  private config: CloudLoggingConfig;
  private logBuffer: LogEntry[] = [];
  private sessionId: string;
  private flushTimer: NodeJS.Timeout | null = null;
  private retryTimer: NodeJS.Timeout | null = null;
  private retryAttempts: number = 0;
  private maxRetryAttempts: number = 5;
  private localStorageDisabled: boolean = false; // Flag to disable local storage if quota exceeded
  private originalConsole: {
    log: typeof console.log;
    warn: typeof console.warn;
    error: typeof console.error;
    info: typeof console.info;
    debug: typeof console.debug;
  };

  constructor(config: CloudLoggingConfig) {
    this.config = config;
    this.sessionId = this.generateSessionId();
    
    // Store original console methods
    this.originalConsole = {
      log: console.log.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
      info: console.info.bind(console),
      debug: console.debug.bind(console),
    };

    this.interceptConsole();
    this.startPeriodicFlush();
    this.setupErrorHandlers();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2)}`;
  }

  private async getAuthToken(): Promise<string | null> {
    try {
      if (auth.currentUser) {
        return await auth.currentUser.getIdToken();
      }
      return null;
    } catch (error) {
      // Silently fail for logging service - don't create logging loops
      return null;
    }
  }

  private interceptConsole(): void {
    if (!this.config.enabled) return;

    // Intercept console.log
    console.log = (...args: any[]) => {
      this.originalConsole.log(...args);
      this.captureLog('info', args.join(' '), 'console.log');
    };

    // Intercept console.warn
    console.warn = (...args: any[]) => {
      this.originalConsole.warn(...args);
      this.captureLog('warn', args.join(' '), 'console.warn');
    };

    // Intercept console.error
    console.error = (...args: any[]) => {
      this.originalConsole.error(...args);
      this.captureLog('error', args.join(' '), 'console.error');
    };

    // Intercept console.info
    console.info = (...args: any[]) => {
      this.originalConsole.info(...args);
      this.captureLog('info', args.join(' '), 'console.info');
    };

    // Intercept console.debug
    console.debug = (...args: any[]) => {
      this.originalConsole.debug(...args);
      this.captureLog('debug', args.join(' '), 'console.debug');
    };
  }

  private setupErrorHandlers(): void {
    if (!this.config.enabled) return;

    // Global error handler
    window.addEventListener('error', (event) => {
      this.captureLog('error', event.message, 'global.error', {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
      });
    });

    // Unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      this.captureLog('error', `Unhandled Promise Rejection: ${event.reason}`, 'promise.rejection', {
        reason: event.reason,
        stack: event.reason?.stack,
      });
    });

    // React error boundary integration
    window.addEventListener('react-error', (event: any) => {
      this.captureLog('error', event.detail.message, 'react.error', {
        componentStack: event.detail.componentStack,
        errorBoundary: event.detail.errorBoundary,
      });
    });
  }

  public captureLog(
    level: LogEntry['level'],
    message: string,
    component: string,
    metadata?: Record<string, any>
  ): void {
    if (!this.config.enabled) return;

    const logEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      source: 'frontend',
      component,
      sessionId: this.sessionId,
      // CSP diagnostics must not inherit booking IDs or other PII from the page URL.
      url: component === 'csp' ? window.location.origin : window.location.href,
      userAgent: navigator.userAgent,
      metadata,
    };

    // Add stack trace for errors
    if (level === 'error') {
      logEntry.stackTrace = new Error().stack;
    }

    this.logBuffer.push(logEntry);

    // Immediate flush for critical errors
    if (level === 'error' || this.logBuffer.length >= this.config.batchSize) {
      this.flushLogs();
    }
  }

  private startPeriodicFlush(): void {
    if (!this.config.enabled) return;

    this.flushTimer = setInterval(() => {
      if (this.logBuffer.length > 0) {
        this.flushLogs();
      }
    }, this.config.flushInterval);
  }

  private async flushLogs(): Promise<void> {
    // The periodic flush will send buffered entries once a tenant is available.
    if (this.logBuffer.length === 0 || !peekTenantRuntime()) return;

    let logsToSend: LogEntry[] = [];

    try {
      // Get auth token for API request
      const authToken = await this.getAuthToken();
      // Resolution can be cleared or changed while the token is being obtained.
      if (!peekTenantRuntime() || this.logBuffer.length === 0) return;
      logsToSend = [...this.logBuffer];
      this.logBuffer = [];
      
      // Send to Google Cloud Logging via our backend endpoint
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Add auth header if token is available
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await fetch(apiUrl(`/api/logs`), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          projectId: this.config.projectId,
          logName: this.config.logName,
          entries: logsToSend,
        }),
        // Add timeout to prevent hanging requests
        signal: AbortSignal.timeout(10000) // 10 second timeout
      });

      if (!response.ok) {
        // Handle specific HTTP error codes
        if ([502, 503, 504].includes(response.status)) {
          // Service temporarily unavailable - retry with exponential backoff
          this.originalConsole.warn(`Cloud logging service unavailable (${response.status}). Will retry later.`);
          this.logBuffer.unshift(...logsToSend); // Put logs back in buffer for retry
          this.scheduleRetry();
        } else if (response.status >= 400 && response.status < 500) {
          // Client error - don't retry, just log locally
          this.originalConsole.error(`Cloud logging client error: ${response.status}. Storing locally only.`);
        } else {
          // Server error - retry later
          this.originalConsole.warn(`Cloud logging server error: ${response.status}. Will retry later.`);
          this.logBuffer.unshift(...logsToSend);
          this.scheduleRetry();
        }
        
        this.storeLogsLocally(logsToSend);
        return;
      }

      // Success - also store locally as backup
      this.storeLogsLocally(logsToSend);
      
      // Reset retry attempts on success
      this.retryAttempts = 0;

    } catch (error) {
      // Handle network errors with better categorization
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        this.originalConsole.warn('Cloud logging network error. Storing locally and will retry.');
        this.logBuffer.unshift(...logsToSend);
        this.scheduleRetry();
      } else if (error instanceof DOMException && error.name === 'TimeoutError') {
        this.originalConsole.warn('Cloud logging request timed out. Will retry later.');
        this.logBuffer.unshift(...logsToSend);
        this.scheduleRetry();
      } else if (error instanceof DOMException && error.name === 'AbortError') {
        this.originalConsole.warn('Cloud logging request was aborted. Will retry later.');
        this.logBuffer.unshift(...logsToSend);
        this.scheduleRetry();
      } else {
        // Unknown error - log details and store locally
        this.originalConsole.error('Unknown cloud logging error:', error);
        this.logBuffer.unshift(...logsToSend);
        this.scheduleRetry();
      }
      
      // Always store locally as fallback (if not disabled)
      if (!this.localStorageDisabled) {
        this.storeLogsLocally(logsToSend);
      }
    }
  }

  /**
   * Schedule retry with exponential backoff
   */
  private scheduleRetry(): void {
    if (this.retryAttempts >= this.maxRetryAttempts) {
      this.originalConsole.warn(`Max retry attempts (${this.maxRetryAttempts}) reached for cloud logging. Disabling retries.`);
      return;
    }

    // Clear any existing retry timer
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
    }

    // Exponential backoff: 2^attempts * 1000ms, max 30 seconds
    const delay = Math.min(Math.pow(2, this.retryAttempts) * 1000, 30000);
    this.retryAttempts++;

    this.originalConsole.log(`Scheduling cloud logging retry attempt ${this.retryAttempts} in ${delay}ms`);

    this.retryTimer = setTimeout(() => {
      if (this.logBuffer.length > 0) {
        this.flushLogs();
      }
    }, delay);
  }

  private storeLogsLocally(logs: LogEntry[]): void {
    // Skip local storage if disabled due to quota issues
    if (this.localStorageDisabled) {
      return;
    }

    try {
      const MAX_STORAGE_SIZE = 4 * 1024 * 1024; // 4MB
      const MAX_LOGS_COUNT = 200; // Reduced from 1000
      
      // Truncate large log entries before storing
      const truncatedLogs = logs.map(log => ({
        ...log,
        message: this.truncateString(log.message, 5000),
        stackTrace: log.stackTrace ? this.truncateString(log.stackTrace, 5000) : undefined,
        metadata: this.truncateMetadata(log.metadata)
      }));
      
      const existingLogs = JSON.parse(localStorage.getItem('cloudLogs') || '[]');
      const allLogs = [...existingLogs, ...truncatedLogs];
      
      // Keep only last N logs
      let trimmedLogs = allLogs.slice(-MAX_LOGS_COUNT);
      
      // Check size and reduce if needed
      let logsJson = JSON.stringify(trimmedLogs);
      while (new Blob([logsJson]).size > MAX_STORAGE_SIZE && trimmedLogs.length > 0) {
        trimmedLogs = trimmedLogs.slice(-Math.floor(trimmedLogs.length * 0.5));
        logsJson = JSON.stringify(trimmedLogs);
      }
      
      localStorage.setItem('cloudLogs', logsJson);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        // Quota exceeded - try to clear old logs and retry with fewer logs
        try {
          const existingLogs = JSON.parse(localStorage.getItem('cloudLogs') || '[]');
          const reducedLogs = existingLogs.slice(-50); // Keep only last 50
          localStorage.setItem('cloudLogs', JSON.stringify(reducedLogs));
          
          // Try storing new logs again (only if small enough)
          if (logs.length <= 10) {
            const truncatedLogs = logs.map(log => ({
              ...log,
              message: this.truncateString(log.message, 5000),
              stackTrace: log.stackTrace ? this.truncateString(log.stackTrace, 5000) : undefined,
              metadata: this.truncateMetadata(log.metadata)
            }));
            const newLogsJson = JSON.stringify([...reducedLogs, ...truncatedLogs]);
            if (new Blob([newLogsJson]).size < 2 * 1024 * 1024) {
              localStorage.setItem('cloudLogs', newLogsJson);
            }
          }
        } catch (retryError) {
          // If still failing, clear all logs and disable local storage
          this.originalConsole.warn('LocalStorage quota exceeded. Clearing all logs and disabling local storage.');
          localStorage.removeItem('cloudLogs');
          this.localStorageDisabled = true; // Disable local storage for this session
        }
      } else {
        this.originalConsole.error('Failed to store logs locally:', error);
      }
    }
  }

  private truncateString(str: string, maxLength: number): string {
    if (!str || str.length <= maxLength) return str;
    return str.substring(0, maxLength) + '...[truncated]';
  }

  private truncateMetadata(metadata?: Record<string, any>): Record<string, any> | undefined {
    if (!metadata) return undefined;
    const metadataStr = JSON.stringify(metadata);
    if (metadataStr.length > 2000) {
      return { _truncated: true, _originalSize: metadataStr.length };
    }
    return metadata;
  }

  public getSessionId(): string {
    return this.sessionId;
  }

  public destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
    }
    
    // Restore original console methods
    console.log = this.originalConsole.log;
    console.warn = this.originalConsole.warn;
    console.error = this.originalConsole.error;
    console.info = this.originalConsole.info;
    console.debug = this.originalConsole.debug;
    
    // Final flush
    this.flushLogs();
  }
}

// Singleton instance
let cloudLoggingInstance: CloudLoggingService | null = null;
// Bounded: before initialization only the first entries are kept (logging may never start, e.g. before login).
const MAX_PENDING_LOGS = 50;
const pendingLogs: Parameters<CloudLoggingService['captureLog']>[] = [];

export const initializeCloudLogging = (config?: Partial<CloudLoggingConfig>): CloudLoggingService => {
  const defaultConfig: CloudLoggingConfig = {
    projectId: 'hotel-support-chat',
    logName: 'frontend-logs',
    batchSize: 10,
    flushInterval: 5000, // 5 seconds
    enabled: import.meta.env.PROD, // Only in production by default
  };

  const finalConfig = { ...defaultConfig, ...config };
  
  if (cloudLoggingInstance) {
    cloudLoggingInstance.destroy();
  }
  
  cloudLoggingInstance = new CloudLoggingService(finalConfig);
  for (const entry of pendingLogs.splice(0)) {
    cloudLoggingInstance.captureLog(...entry);
  }
  
  // Expose logToCloud function to window for testing and direct access
  if (typeof window !== 'undefined') {
    (window as any).logToCloud = logToCloud;
    (window as any).getCloudLogging = getCloudLogging;
    console.log('✅ Cloud logging functions exposed to window.logToCloud and window.getCloudLogging');
  }
  
  return cloudLoggingInstance;
};

export const getCloudLogging = (): CloudLoggingService | null => {
  return cloudLoggingInstance;
};

export const logToCloud = (
  level: LogEntry['level'],
  message: string,
  component: string,
  metadata?: Record<string, any>
) => {
  if (cloudLoggingInstance) {
    cloudLoggingInstance.captureLog(level, message, component, metadata);
  } else if (pendingLogs.length < MAX_PENDING_LOGS) {
    pendingLogs.push([level, message, component, metadata]);
  }
};

export type { LogEntry, CloudLoggingConfig };
